import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle, Check } from 'lucide-react';
import { 
  validateImageFile, 
  getLogoPreviewUrl, 
  cleanupPreviewUrl,
  LOGO_CONSTRAINTS 
} from '../utils/logoService';

const LogoUpload = ({ 
  onFileSelect, 
  onFileRemove, 
  initialLogoUrl = null,
  disabled = false,
  showConstraints = true,
  className = ""
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(initialLogoUrl);
  const [isDragging, setIsDragging] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Update preview URL when initialLogoUrl prop changes
  useEffect(() => {
    if (initialLogoUrl !== previewUrl && !selectedFile) {
      // Clean up previous preview if it's a blob URL
      if (previewUrl && previewUrl !== initialLogoUrl && previewUrl.startsWith('blob:')) {
        cleanupPreviewUrl(previewUrl);
      }
      setPreviewUrl(initialLogoUrl);
    }
  }, [initialLogoUrl, previewUrl, selectedFile]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl !== initialLogoUrl) {
        cleanupPreviewUrl(previewUrl);
      }
    };
  }, [previewUrl, initialLogoUrl]);

  const handleFileSelect = (file) => {
    // Validate file
    const validation = validateImageFile(file);
    setValidationErrors(validation.errors);

    if (!validation.isValid) {
      return;
    }

    // Clean up previous preview
    if (previewUrl && previewUrl !== initialLogoUrl) {
      cleanupPreviewUrl(previewUrl);
    }

    // Set new file and preview
    setSelectedFile(file);
    const newPreviewUrl = getLogoPreviewUrl(file);
    setPreviewUrl(newPreviewUrl);

    // Notify parent component
    if (onFileSelect) {
      onFileSelect(file);
    }
  };

  const handleFileRemove = () => {
    // Clean up preview
    if (previewUrl && previewUrl !== initialLogoUrl) {
      cleanupPreviewUrl(previewUrl);
    }

    setSelectedFile(null);
    setPreviewUrl(initialLogoUrl);
    setValidationErrors([]);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Notify parent component
    if (onFileRemove) {
      onFileRemove();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-colors
          ${isDragging 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={LOGO_CONSTRAINTS.allowedTypes.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        {previewUrl ? (
          /* Preview */
          <div className="space-y-4">
            <div className="relative inline-block">
              <img
                src={previewUrl}
                alt="Logo preview"
                className="max-w-32 max-h-32 object-contain mx-auto rounded border"
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFileRemove();
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {selectedFile && (
              <div className="text-sm text-gray-600">
                <p className="font-medium">{selectedFile.name}</p>
                <p>{formatFileSize(selectedFile.size)}</p>
              </div>
            )}
            {!disabled && (
              <p className="text-sm text-gray-500">
                Click to change or drag a new image here
              </p>
            )}
          </div>
        ) : (
          /* Upload prompt */
          <div className="space-y-3">
            <div className="flex justify-center">
              {isDragging ? (
                <Upload className="w-12 h-12 text-blue-500" />
              ) : (
                <ImageIcon className="w-12 h-12 text-gray-400" />
              )}
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">
                {isDragging ? 'Drop your logo here' : 'Upload organization logo'}
              </p>
              <p className="text-sm text-gray-500">
                Drag and drop or click to browse
              </p>
            </div>
          </div>
        )}

        {/* Upload progress indicator */}
        {isUploading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Uploading...</p>
            </div>
          </div>
        )}
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">
                Please fix the following issues:
              </h3>
              <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Constraints Info */}
      {showConstraints && (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <div className="flex">
            <Check className="w-5 h-5 text-green-400 mt-0.5 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-gray-800">
                Logo Requirements:
              </h3>
              <ul className="mt-1 text-sm text-gray-600 space-y-1">
                <li>• Maximum size: {LOGO_CONSTRAINTS.maxFileSize / (1024 * 1024)}MB</li>
                <li>• Maximum dimensions: {LOGO_CONSTRAINTS.maxWidth}×{LOGO_CONSTRAINTS.maxHeight}px</li>
                <li>• Supported formats: JPG, PNG, WebP</li>
                <li>• Aspect ratio will be maintained</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogoUpload; 